import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hammer, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { LEGAL_TEXT } from "@/lib/labels";

const HERO = "https://static.prod-images.emergentagent.com/jobs/05d27c20-0691-4338-8744-e43c92ae9ab0/images/3e1fda3ee0712083cf015212feece995231fca7251a3d821830b68cc987ab075.png";

export default function Auth() {
  const { loginSuccess } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await api.post(endpoint, payload);
      if (mode === "register") sessionStorage.setItem("bottega_onboarding", "1");
      loginSuccess(data);
      toast.success(mode === "login" ? "Bentornato in Bottega!" : "Sei entrato in Bottega!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Statua pop-art" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2a0d10]/90 via-[#2a0d10]/25 to-[#2a0d10]/40" />
        {/* Adesivo promo pop */}
        <div data-testid="promo-trial-hero" className="absolute top-8 right-8 z-10 rotate-3 max-w-[250px]">
          <div className="rounded-2xl bg-accent text-accent-foreground px-4 py-3 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.55)] border-2 border-white/70">
            <div className="flex items-center gap-2">
              <PartyPopper className="h-5 w-5 shrink-0" strokeWidth={2} />
              <span className="font-serif font-bold text-base leading-tight">Il primo mese è gratis</span>
            </div>
            <p className="text-xs font-semibold mt-1 pl-7">Se ti annoi non paghi nulla!</p>
          </div>
        </div>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <Hammer className="h-8 w-8" strokeWidth={1.5} />
            <span className="font-serif text-2xl font-bold">Bottega di Filosofia</span>
          </div>
          <div>
            <h1 className="font-serif text-5xl font-bold leading-tight mb-4">
              Entra nella<br />Bottega di Filosofia.
            </h1>
            <p className="text-white/80 text-lg max-w-md leading-relaxed">
              Per te che stai studiando la filosofia e hai bisogno di un compendio easy to use,
              o semplicemente per te che ti avvicini alla materia per diletto.
            </p>
          </div>
          <p className="text-white/50 text-sm">"Sapere aude" — Abbi il coraggio di sapere.</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden -mx-6 sm:-mx-12 -mt-6 sm:-mt-12 mb-8 relative">
            <img src={HERO} alt="Statua pop-art" className="w-full h-52 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2a0d10]/95 via-[#2a0d10]/35 to-transparent" />
            <div className="absolute bottom-4 left-6 right-6 text-white">
              <div className="flex items-center gap-2">
                <Hammer className="h-6 w-6" strokeWidth={1.5} />
                <span className="font-serif text-lg font-bold">Bottega di Filosofia</span>
              </div>
            </div>
          </div>

          <h2 className="font-serif text-3xl font-bold mb-1">
            {mode === "login" ? "Bentornato in Bottega" : "Entra in Bottega"}
          </h2>
          <p className="text-muted-foreground text-sm mb-6">
            {mode === "login"
              ? "Riprendi il tuo percorso tra i Maestri."
              : "Inizia il tuo percorso in filosofia."}
          </p>

          {/* Adesivo promo pop (visibile su tutti gli schermi, in primo piano su mobile) */}
          <div data-testid="promo-trial" className="mb-7 -rotate-1">
            <div className="inline-flex items-center gap-2.5 rounded-xl bg-accent text-accent-foreground px-4 py-2.5 shadow-[0_10px_30px_-12px_rgba(114,47,55,0.6)] border border-black/5">
              <PartyPopper className="h-5 w-5 shrink-0" strokeWidth={2} />
              <div className="leading-tight text-left">
                <p className="font-serif font-bold text-sm">Il primo mese è gratis</p>
                <p className="text-[11px] font-semibold opacity-90">Se ti annoi non paghi nulla!</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nome e cognome</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1.5 rounded-sm"
                  placeholder="Mario Rossi"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5 rounded-sm"
                placeholder="tua@email.it"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="mt-1.5 rounded-sm"
                placeholder="La tua password"
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full rounded-sm bg-primary hover:bg-primary/90 h-11"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Entra" : "Crea il tuo posto"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              data-testid="toggle-mode-btn"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "login" ? "Entra in Bottega" : "Accedi"}
            </button>
          </p>

          <p data-testid="legal-disclaimer-auth" className="mt-6 text-[11px] leading-relaxed text-muted-foreground">
            {LEGAL_TEXT}
          </p>
        </div>
      </div>
    </div>
  );
}
